import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrencyCurrency, Product } from '@linktivity/ars-neo';
import { Down } from '@linktivity/link-icons';
import { Button } from '@linktivity/link-ui';
import VerticalProductCard from '@link/share/business/VerticalProductCard';
import { buildLocalizedPath, resolveProductPriceInfo } from '@link/share/utils';
import styles from './list.module.css';

const PRODUCT_SHOW_NUMBER = 4;

type Props = {
  products: Product[];
  locale: string;
  currency: CurrencyCurrency;
  showOtherCurrencies: boolean;
};

/**
 * A product row that renders its first four cards and expands on demand.
 *
 * All four cards are in the server HTML, so crawlers and the first paint get
 * the products without JavaScript; only the expand toggle needs hydration.
 *
 * `locale` and `currency` arrive as props rather than being read from a store
 * or the i18n singleton: both are per-request values, and reading them from
 * module state would let one visitor's render pick up another's.
 */
export default function ExpandableProductList({
  products,
  locale,
  currency,
  showOtherCurrencies
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Collapse again when the row's contents change (locale or currency switch).
  useEffect(() => {
    setExpanded(false);
  }, [products]);

  const visibleProducts = expanded
    ? products
    : products.slice(0, PRODUCT_SHOW_NUMBER);
  const showViewMore = products.length > PRODUCT_SHOW_NUMBER && !expanded;

  return (
    <>
      <div className={styles.productList}>
        {visibleProducts.map(product => {
          const priceInfo = resolveProductPriceInfo(
            product,
            product.amount ?? 0,
            currency,
            showOtherCurrencies
          );
          const activity = product.productContent?.activity;
          return (
            <VerticalProductCard
              key={product.id}
              image={activity?.imageUrls?.[0] ?? ''}
              title={activity?.title ?? ''}
              tags={
                product.tags
                  ?.map(tag => tag.content?.name)
                  .filter((name): name is string => !!name) ?? []
              }
              priceInfo={priceInfo}
              to={
                product.id
                  ? buildLocalizedPath(`product/${product.id}`, locale)
                  : ''
              }
            />
          );
        })}
      </div>
      {showViewMore ? (
        <div className={styles.viewMoreRow}>
          <Button
            type="button"
            variant="text-secondary"
            onClick={() => setExpanded(true)}
          >
            {t('triplabo.home.viewMore')}
            <Down className={styles.arrowIcon} />
          </Button>
        </div>
      ) : null}
    </>
  );
}
